import { ACCESS_BONUS, ACCESS_MY_BETS, HEIGHT_SKELETON_SLUG_BONUS, VETRINA_LOTTERY } from './constants';
import {
  CashoutResponseApiWithTimer,
  setBlockedIdsRealTimeCashout,
  setIdsRealTimeCashout,
  setTicketCashout,
} from 'features/dashboard/myBets/myBetSlice';
import { Dispatch, MutableRefObject } from 'react';
import { FROM_REGISTRATION, IS_LOGGED, isHappybetSite, isRealTimeCashoutEnabled } from 'utility/constant';
import Persist, { StorageKind } from 'lib/persist';
import {
  SUPPLIER_GRATTA_E_VINCI_MODEL_API_KEY,
  SUPPLIER_LOTTERIA_ITALIA_MODEL_API_KEY,
  SUPPLIER_LOTTOMATICA_MODEL_API_KEY,
  SUPPLIER_SISAL_MODEL_API_KEY,
} from 'lib/datoCms/constants';
import { getAmount, isCORS, isSeamlessUrl, isTruthy } from 'utility/functions';

import { AnyAction } from '@reduxjs/toolkit';
import { ButtonField } from 'components/mieScommesseTableRow/buttonField/ButtonField';
import { CashOutButton } from 'components/cashOutButton';
import { DatoCmsMyBetsPage } from './../../lib/datoCms/queries/getMyBetsPage';
import { DeepPartial } from 'types/swagger';
import { MieScommesseEvent } from 'components/mieScommesseTableRow/mieScommesseEvent/mieScomesseEvent';
import { MyAutoesclusionCms } from 'lib/datoCms/queries/getMyAutoesclusionPage';
import { MyBet } from 'types/dashboard/myBet';
import { MyBonusCms } from 'lib/datoCms/queries/getMyBonusPage';
import { MyLimitsCms } from 'lib/datoCms/queries/getMyLimitsPage';
import { NextRouter } from 'next/router';
import { PollingCashoutResponseApi } from 'lib/api/pollingCashoutResponseApi';
import { ScommesseResponseApiItem } from 'lib/api/scommesseResponseApi';
import { Session } from 'next-auth';
import { StateField } from 'components/mieScommesseTableRow/stateField/StateField';
import { SystemProps } from 'components/detailedBet/DetailedBet';
import { TableField } from 'components/mieScommesseTableRow/tableField/TableField';
import { TableFieldProps } from 'components/mieScommesseTableRow/MieScommesseTableRow';
import add from 'date-fns/add';
import format from 'date-fns/format';
import { getTokenBySupplierType } from 'lib/launchGame/utils';

const cookie = Persist(StorageKind.cookie);
const sessionStorage = Persist(StorageKind.session);

const formatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
});

export const getStatusCardBonus = (
  status: number | null,
  labels: {
    labelAssegnato: string;
    labelErogato: string;
    labelScaduto: string;
    labelDaRiscattare: string;
    labelRiscattato: string;
    labelStornato: string;
    labelRifiutato: string;
    labelScadutoNonAccettato: string;
    labelNonErogato: string;
  }
) => {
  const STATUS_BONUS: { [key: number]: string } = {
    0: labels.labelAssegnato,
    1: labels.labelErogato,
    2: labels.labelScaduto,
    3: labels.labelDaRiscattare,
    4: labels.labelRiscattato,
    5: labels.labelStornato,
    6: labels.labelRifiutato,
    7: labels.labelScadutoNonAccettato,
    255: labels.labelNonErogato,
  };
  if (!status) return '-';
  return STATUS_BONUS[status];
};

export const getAccessCardBonus = (
  access: string | null
): 'listFunbonus' | 'listFreespin' | 'listBonusGold' | 'listBonusCash' | '-' => {
  if (!access) return '-';
  return ACCESS_BONUS[access];
};

export const getHeightSkeletonSlugBonus = (slug: string | null) => {
  if (!slug) return '0';
  return HEIGHT_SKELETON_SLUG_BONUS[slug];
};

export const getSubtitleBonus = (slug: string | null, myBonusPage: MyBonusCms) => {
  if (!slug) return '';
  switch (slug) {
    case 'gold':
      return myBonusPage.descrizioneBonusGold;
    default:
      return '';
  }
};

export const getLabelLimits = (label: string | null, myLimitsPage: MyLimitsCms) => {
  if (!label) return '';
  switch (label) {
    case 'Importo massimo totale di gioco':
      return myLimitsPage.importoMassimoGiornaliero;
    case 'Importo massimo per singola giocata':
      return myLimitsPage.importoMassimoGiocata;
    case 'Importo massimo ricariche in una settimana':
      return myLimitsPage.importoMassimoSettimanale;
    default:
      return label;
  }
};

export const getPlaceholderLimits = (label: string | null, myLimitsPage: MyLimitsCms) => {
  if (!label) return '';
  switch (label) {
    case 'Importo massimo totale di gioco':
      return myLimitsPage.placeholderImportoMassimoGiornaliero;
    case 'Importo massimo per singola giocata':
      return myLimitsPage.placeholderImportoMassimoGiocata;
    case 'Importo massimo ricariche in una settimana':
      return myLimitsPage.placeholderImportoMassimoSettimanale;
    default:
      return label;
  }
};

export const populateItemMieScommesseDashboardRow = ({
  item,
  labels,
  dispatch,
  blockedIdsRealTimeCashoutRef,
  idsRealTimeCashoutRef,
  cashoutResponse,
  blockedIdsRealTimeCashout,
  labelCashOut,
}: {
  item: ScommesseResponseApiItem;
  labels: {
    labelSingola?: string;
    labelEventi?: string;
    labelPossibileVincita?: string;
    labelImportoGiocato?: string;
    labelSport?: string;
    labelSistema?: string;
    labelMultipla?: string;
    labelVerificaCashout?: string;
    labelConfermaCashout?: string;
  };
  dispatch: Dispatch<AnyAction>;
  labelCashOut?: string;
  blockedIdsRealTimeCashoutRef: MutableRefObject<CashoutResponseApiWithTimer[]>;
  idsRealTimeCashoutRef: MutableRefObject<string[]>;
  cashoutResponse?: PollingCashoutResponseApi;
  blockedIdsRealTimeCashout?: CashoutResponseApiWithTimer[];
}) => {
  const data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: item.type, //labels.labelSport,
        className: 'accent',
        value: getTypologyByString(item.tipologia, labels),
      },
    },
    {
      component: TableField,
      props: {
        label: labels.labelImportoGiocato,
        value: formatter.format(getAmount(item.importo)),
        internalClassName: item.tipologia === 'Sistema' ? 'span2' : '',
      },
    },
  ];
  if (item.tipologia !== 'Sistema') {
    data.push({
      component: TableField,
      props: {
        label: labels.labelPossibileVincita,
        value: formatter.format(getAmount(item.vincita)),
      },
    });
  }
  data.push({
    component: MieScommesseEvent,
    props: {
      label: labels.labelEventi,
      event: {
        win: item.eventi?.vintiCount,
        live: item.eventi?.liveCount,
        programmed: item.eventi?.programmatiCount,
        lose: item.eventi?.persiCount,
      },
    },
  });
  if (!isRealTimeCashoutEnabled && item.hasCashout) {
    data.push({
      component: ButtonField,
      props: {
        label: labels.labelVerificaCashout,
        onClick: () => {
          dispatch(
            setTicketCashout({
              idBiglietto: item.idBiglietto ? item.idBiglietto : undefined,
              importo: item.importoCashout ? Number(item.importoCashout) : undefined,
            })
          );
        },
      },
    });
  } else {
    const findedCashout = cashoutResponse && item.idBiglietto ? cashoutResponse[item.idBiglietto] : undefined;
    const elIsBlockedCashout = blockedIdsRealTimeCashout?.find((el) => el.idBiglietto === item.idBiglietto);
    if (item.hasCashout && isRealTimeCashoutEnabled && findedCashout?.isAbilitato) {
      data.push({
        component: CashOutButton,
        props: {
          label: elIsBlockedCashout
            ? `${labels.labelConfermaCashout} ${formatter.format(getAmount(elIsBlockedCashout?.importo))}`
            : `${labelCashOut} ${formatter.format(getAmount(findedCashout?.importo))}`,
          internalClassName: elIsBlockedCashout ? 'blockedCashout' : undefined,
          isBlocked: !!elIsBlockedCashout,
          idBiglietto: item.idBiglietto,
          importo: elIsBlockedCashout?.importo,
          onClick: () => {
            if (!!elIsBlockedCashout) {
              clearTimeout(elIsBlockedCashout?.timer);
              // const newIds = blockedIdsRealTimeCashoutRef
              //   ? blockedIdsRealTimeCashoutRef?.current.filter((el) => el.idBiglietto !== findedCashout.idBiglietto)
              //   : [];
              // dispatch(setBlockedIdsRealTimeCashout(newIds));
              // blockedIdsRealTimeCashoutRef.current = newIds;
            } else {
              if (blockedIdsRealTimeCashout && findedCashout) {
                const timer = setTimeout(() => {
                  const newIds = blockedIdsRealTimeCashoutRef
                    ? blockedIdsRealTimeCashoutRef?.current.filter((el) => el.idBiglietto !== findedCashout.idBiglietto)
                    : [];
                  dispatch(setBlockedIdsRealTimeCashout(newIds));
                  blockedIdsRealTimeCashoutRef.current = newIds;
                }, 5000);
                dispatch(setBlockedIdsRealTimeCashout([...blockedIdsRealTimeCashout, { ...findedCashout, timer }]));
                blockedIdsRealTimeCashoutRef.current = [...blockedIdsRealTimeCashout, { ...findedCashout, timer }];
              }
            }
          },
        },
      });
    }
  }
  return data;
};

export const populateItemMieScommesseRow = ({
  item,
  labels,
  labelCashOut,
  dispatch,
  blockedIdsRealTimeCashoutRef,
  idsRealTimeCashoutRef,
  isInColumn,
  selectedStatusTab,
  isMobile,
  cashoutResponse,
  blockedIdsRealTimeCashout,
  refetchApiBets,
  isNotDashboardScommesse,
}: {
  item: ScommesseResponseApiItem;
  labels: {
    labelSingola?: string;
    labelSistema?: string;
    labelMultipla?: string;
    labelImportoGiocato?: string;
    labelQuotaTotale?: string;
    labelPossibileVincita?: string;
    labelEventi?: string;
    labelEsiti?: string;
    labelVerificaCashout?: string;
    labelStato?: string;
    labelVincita?: string;
    labelEmesso?: string;
    labelConfermaCashout?: string;
  };
  labelCashOut: string;
  dispatch: Dispatch<AnyAction>;
  blockedIdsRealTimeCashoutRef: MutableRefObject<CashoutResponseApiWithTimer[]>;
  idsRealTimeCashoutRef: MutableRefObject<string[]>;
  isInColumn?: boolean;
  selectedStatusTab?: string;
  isMobile?: boolean;
  cashoutResponse?: PollingCashoutResponseApi;
  blockedIdsRealTimeCashout?: CashoutResponseApiWithTimer[];
  refetchApiBets?: () => void;
  isNotDashboardScommesse?: boolean;
}) => {
  const data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: item.dataEmissione ? format(new Date(item.dataEmissione), 'dd/MM/yyyy HH:mm') : undefined,
        classNameData: !isNotDashboardScommesse ? 'value' : undefined,
        internalClassName: 'noMargin',
        value: getTypologyByString(item.tipologia, labels),
        isInColumn,
      },
    },
    {
      component: TableField,
      props: {
        value: '', //TODO: inserire i match come stringa
        internalClassName: isMobile || isInColumn ? 'span2' : undefined,
        classNameData: 'matches',
      },
    },
    {
      component: TableField,
      props: {
        label: labels.labelImportoGiocato,
        value: formatter.format(getAmount(item.importo)),
        isInColumn,
      },
    },
  ];

  if (item.tipologia !== 'Sistema') {
    data.push({
      component: TableField,
      props: {
        label: labels.labelPossibileVincita,
        value: isPositive(item.vincitaPotenziale) ? formatter.format(getAmount(item.vincitaPotenziale)) : '-',
        isInColumn,
        internalClassName:
          selectedStatusTab === 'Chiusa' &&
          trasformStateHappybet(item.stato) !== 'Chiusa con cash out' &&
          trasformStateHappybet(item.stato) !== 'Vinta' &&
          !item.eventi
            ? !isMobile && !isInColumn
              ? 'span2'
              : undefined
            : undefined,
      },
    });
  } else {
    data.push({
      component: TableField,
      props: {
        label: labels.labelPossibileVincita,
        value: isPositive(item.vincitaPotenziale) ? formatter.format(getAmount(item.vincitaPotenziale)) : '-',
        isInColumn,
        internalClassName:
          selectedStatusTab === 'Chiusa' &&
          trasformStateHappybet(item.stato) !== 'Chiusa con cash out' &&
          trasformStateHappybet(item.stato) !== 'Vinta' &&
          !item.eventi
            ? !isMobile && !isInColumn
              ? 'span2'
              : undefined
            : undefined,
      },
    });
  }

  if (item.eventi) {
    data.push({
      component: MieScommesseEvent,
      props: {
        label: item.tipologia === 'Sistema' ? labels.labelEsiti : labels.labelEventi,
        event: {
          win: item.eventi?.vintiCount,
          live: item.eventi?.liveCount,
          programmed: item.eventi?.programmatiCount,
          lose: item.eventi?.persiCount,
        },
        isInColumn,
        //noMargin: isInColumn,
      },
    });
  }

  if (selectedStatusTab === 'Chiusa') {
    if (trasformStateHappybet(item.stato) === 'Chiusa con cash out' || trasformStateHappybet(item.stato) === 'Vinta') {
      data.push({
        component: TableField,
        props: {
          label: trasformStateHappybet(item.stato) === 'Vinta' ? labels.labelVincita : labelCashOut,
          value:
            trasformStateHappybet(item.stato) === 'Vinta'
              ? formatter.format(getAmount(item.vincita))
              : formatter.format(getAmount(item.importoCashout)),
          isInColumn,
        },
      });
    }
    data.push({
      component: StateField,
      props: {
        label: labels.labelStato,
        value: item.stato,
        isInColumn,
      },
    });
  }
  if (!isRealTimeCashoutEnabled && item.hasCashout) {
    data.push({
      component: ButtonField,
      props: {
        label: labels.labelVerificaCashout,
        onClick: () => {
          dispatch(
            setTicketCashout({
              idBiglietto: item.idBiglietto ? item.idBiglietto : undefined,
              importo: item.importoCashout ? Number(item.importoCashout) : undefined,
            })
          );
        },
        isInColumn,
      },
    });
  } else {
    const findedCashout = cashoutResponse && item.idBiglietto ? cashoutResponse[item.idBiglietto] : undefined;
    const elIsBlockedCashout = blockedIdsRealTimeCashout?.find((el) => el.idBiglietto === item.idBiglietto);
    if ((item.hasCashout && isRealTimeCashoutEnabled && findedCashout?.isAbilitato) || elIsBlockedCashout) {
      data.push({
        component: CashOutButton,
        props: {
          label: elIsBlockedCashout
            ? `${labels.labelConfermaCashout} ${formatter.format(getAmount(elIsBlockedCashout?.importo))}`
            : `${labelCashOut} ${formatter.format(getAmount(findedCashout?.importo))}`,
          internalClassName: elIsBlockedCashout ? 'blockedCashout' : undefined,
          isBlocked: !!elIsBlockedCashout,
          idBiglietto: item.idBiglietto,
          importo: elIsBlockedCashout?.importo,
          refetchApiBets: refetchApiBets,
          onClick: () => {
            if (!!elIsBlockedCashout) {
              clearTimeout(elIsBlockedCashout?.timer);
            } else {
              if (blockedIdsRealTimeCashout && findedCashout) {
                const timer = setTimeout(() => {
                  const newIds = blockedIdsRealTimeCashoutRef
                    ? blockedIdsRealTimeCashoutRef?.current.filter((el) => el.idBiglietto !== findedCashout.idBiglietto)
                    : [];
                  dispatch(setBlockedIdsRealTimeCashout(newIds));
                  blockedIdsRealTimeCashoutRef.current = newIds;
                  const newIdsRealTimeCashout = [...idsRealTimeCashoutRef.current, findedCashout.idBiglietto];
                  idsRealTimeCashoutRef.current = newIdsRealTimeCashout;
                  dispatch(setIdsRealTimeCashout(newIdsRealTimeCashout));
                }, 5000);
                dispatch(setBlockedIdsRealTimeCashout([...blockedIdsRealTimeCashout, { ...findedCashout, timer }]));
                blockedIdsRealTimeCashoutRef.current = [...blockedIdsRealTimeCashout, { ...findedCashout, timer }];
                const newIdsRealTimeCashout = idsRealTimeCashoutRef
                  ? idsRealTimeCashoutRef?.current.filter((el) => el !== findedCashout.idBiglietto)
                  : [];
                idsRealTimeCashoutRef.current = newIdsRealTimeCashout;
                dispatch(setIdsRealTimeCashout(newIdsRealTimeCashout));
              }
            }
          },
          onError: () => {
            const newIds = blockedIdsRealTimeCashoutRef
              ? blockedIdsRealTimeCashoutRef?.current.filter((el) => el.idBiglietto !== elIsBlockedCashout?.idBiglietto)
              : [];
            dispatch(setBlockedIdsRealTimeCashout(newIds));
            blockedIdsRealTimeCashoutRef.current = newIds;
            if (elIsBlockedCashout) {
              const newIdsRealTimeCashout = [...idsRealTimeCashoutRef.current, elIsBlockedCashout.idBiglietto];
              idsRealTimeCashoutRef.current = newIdsRealTimeCashout;
              dispatch(setIdsRealTimeCashout(newIdsRealTimeCashout));
            }
          },
          onSuccess: () => {
            const newIds = blockedIdsRealTimeCashoutRef
              ? blockedIdsRealTimeCashoutRef?.current.filter((el) => el.idBiglietto !== elIsBlockedCashout?.idBiglietto)
              : [];
            dispatch(setBlockedIdsRealTimeCashout(newIds));
            blockedIdsRealTimeCashoutRef.current = newIds;
          },
          isInColumn,
        },
      });
    }
  }

  if (
    item.codiceTipoGioco == '3' || //ippica totalizzatore
    item.codiceTipoGioco == '4' || //ippica quota fissa
    item.codiceTipoGioco == '131'
  ) {
    //ippica nazionale
    data.push({
      component: StateField,
      props: {
        label: labels.labelStato,
        value: item.stato,
        isInColumn,
        //noMargin: isInColumn,
      },
    });
  }
  return data;
};

export const populateItemIppicaRow = (
  item: ScommesseResponseApiItem,
  labels: {
    labelSistema?: string;
    labelMultipla?: string;
    labelImportoGiocato?: string;
    labelQuotaTotale?: string;
    labelPossibileVincita?: string;
    labelEventi?: string;
    labelEsiti?: string;
    labelVerificaCashout?: string;
    labelStato?: string;
    labelVincita?: string;
    labelEmesso?: string;
  }
) => {
  const data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: undefined,
        className: undefined,
        value: item.tipologia?.toUpperCase(), //'TOTALIZZATORE',
      },
    },
    // {
    //   component: TableField,
    //   props: {
    //     label: 'tipo scommessa',
    //     value: 'TRIO',
    //   },
    // },
    {
      component: TableField,
      props: {
        label: labels.labelImportoGiocato,
        value: formatter.format(getAmount(item.importo)),
      },
    },
  ];

  if (item.dataEmissione) {
    data.push({
      component: TableField,
      props: {
        label: labels.labelEmesso,
        value: format(new Date(item.dataEmissione), 'dd/MM/yyyy HH:mm'),
      },
    });
  }
  if (item.stato) {
    data.push({
      component: StateField,
      props: {
        label: labels.labelStato,
        value: item.stato,
      },
    });
  }
  return data;
};

export const populateDetailedItemMieScommesseRow = (
  item: MyBet,
  myBetPage: DatoCmsMyBetsPage,
  labels: {
    labelVincita: string;
    labelBetOpen: string;
  },
  quotaTotale: number,
  eventi: {
    vintiCount: number;
    persiCount: number;
    liveCount: number;
    programmatiCount: number;
  }
) => {
  let data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: myBetPage.labelImportoGiocato,
        className: undefined,
        value: formatter.format(getAmount(item.importo)),
        internalClassName:
          item.tipologia === 'Sistema'
            ? 'span2'
            : item.tipologia === 'Totalizzatore' || item.tipologia === 'Nazionale'
            ? 'span3'
            : undefined,
      },
    },
  ];
  if (item.tipologia !== 'Sistema' && item.tipologia !== 'Totalizzatore' && item.tipologia !== 'Nazionale') {
    data = [
      ...data,
      {
        component: TableField,
        props: {
          label: myBetPage.labelQuotaTotale,
          value: isPositive(quotaTotale) ? parseFloat(getAmount(quotaTotale).toString()).toFixed(2) : '-',
        },
      },
      {
        component: TableField,
        props: {
          label: myBetPage.labelPossibileVincita,
          value: isPositive(item.vincitaPotenziale) ? formatter.format(getAmount(item.vincitaPotenziale)) : '-',
          internalClassName:
            trasformStateHappybet(item.stato) === 'Chiusa con cash out' ||
            trasformStateHappybet(item.stato) === 'Vinta' ||
            item.stato === 'Aperta'
              ? undefined
              : 'span2',
        },
      },
    ];
  } else if (item.tipologia !== 'Totalizzatore' && item.tipologia !== 'Nazionale') {
    data = [
      ...data,
      {
        component: TableField,
        props: {
          label: myBetPage.labelPossibileVincita,
          value: isPositive(item.vincitaPotenziale) ? formatter.format(getAmount(item.vincitaPotenziale)) : '-',
          internalClassName:
            trasformStateHappybet(item.stato) === 'Chiusa con cash out' ||
            trasformStateHappybet(item.stato) === 'Vinta' ||
            item.stato === 'Aperta'
              ? undefined
              : 'span2',
        },
      },
    ];
  }
  if (trasformStateHappybet(item.stato) === 'Chiusa con cash out' || trasformStateHappybet(item.stato) === 'Vinta') {
    data = [
      ...data,
      {
        component: TableField,
        props: {
          label: trasformStateHappybet(item.stato) === 'Vinta' ? labels.labelVincita : myBetPage.cashOut,
          value:
            trasformStateHappybet(item.stato) === 'Vinta'
              ? formatter.format(getAmount(item.vincita))
              : formatter.format(getAmount(item.importoCashout)),
        },
      },
    ];
  }
  if (eventi) {
    data = [
      ...data,
      {
        component: MieScommesseEvent,
        props: {
          label: item.tipologia === 'Sistema' ? myBetPage.labelEsitiSistemi : myBetPage.labelEventi,
          event: {
            win: eventi?.vintiCount,
            live: eventi?.liveCount,
            programmed: eventi?.programmatiCount,
            lose: eventi?.persiCount,
          },
        },
      },
    ];
  }
  data = [
    ...data,
    {
      component: StateField,
      props: {
        label: myBetPage.labelStato,
        value: trasformStateinHappybetLang(labels.labelBetOpen, item.stato),
      },
    },
  ];
  return data;
};

export const populateDetailedItemMieScommesse = (
  item: MyBet,
  myBetPage: DatoCmsMyBetsPage,
  labels: {
    labelSingola: string;
    labelMultipla: string;
    labelSistema: string;
  },
  cardNumber?: string
) => {
  const data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: item.verticale,
        className: 'accent',
        value: getTypologyByString(item.tipologia, labels),
      },
    },

    {
      component: TableField,
      props: {
        label: myBetPage.labelIdBiglietto,
        value: item.idBiglietto,
        classNameData: 'lowerCase',
      },
    },
    {
      component: TableField,
      props: {
        label: myBetPage.labelConto,
        value: cardNumber,
      },
    },
    {
      component: TableField,
      props: {
        label: myBetPage.labelEmesso,
        value: format(new Date(item.dataEmissione!), 'dd/MM/yyyy HH:mm'),
      },
    },
    {
      component: TableField,
      props: {
        label: myBetPage.labelAggiornato,
        value: item.dataModifica && format(new Date(item.dataModifica!), 'dd/MM/yyyy HH:mm'),
      },
    },
  ];

  return data;
};

export const populateStatusBetting = (slug: string) => {
  switch (slug.toLowerCase()) {
    case 'aperte':
      return 'aperta';
    case 'chiuse':
      return 'chiusa';
    default:
      return slug;
  }
};

export const getPropAccessMyBetsList = (
  access: string | null
): 'scommesseAperte' | 'scommesseChiuse' | 'scommesseLive' | '-' => {
  if (!access) return '-';
  return ACCESS_MY_BETS[access];
};
export const getAutoesclusionString = (period: string, myAutoesclusionPage: MyAutoesclusionCms, amount?: string) => {
  switch (period) {
    case 'OneMonth': {
      let stringReplaced = myAutoesclusionPage.labelAutoesclusioneConclusa.replace(
        '${date}',
        format(
          add(new Date(), {
            months: 1,
          }),
          'dd/MM/yyyy'
        )
      );
      stringReplaced = amount ? stringReplaced.replace('${amount}', amount) : stringReplaced;
      return stringReplaced;
    }
    case 'TwoMonths': {
      let stringReplaced = myAutoesclusionPage.labelAutoesclusioneConclusa.replace(
        '${date}',
        format(
          add(new Date(), {
            months: 2,
          }),
          'dd/MM/yyyy'
        )
      );
      stringReplaced = amount ? stringReplaced.replace('${amount}', amount) : stringReplaced;
      return stringReplaced;
    }
    case 'ThreeMonths': {
      let stringReplaced = myAutoesclusionPage.labelAutoesclusioneConclusa.replace(
        '${date}',
        format(
          add(new Date(), {
            months: 3,
          }),
          'dd/MM/yyyy'
        )
      );
      stringReplaced = amount ? stringReplaced.replace('${amount}', amount) : stringReplaced;
      return stringReplaced;
    }
    case 'SixMonths': {
      let stringReplaced = myAutoesclusionPage.labelAutoesclusioneConclusa.replace(
        '${date}',
        format(
          add(new Date(), {
            months: 6,
          }),
          'dd/MM/yyyy'
        )
      );
      stringReplaced = amount ? stringReplaced.replace('${amount}', amount) : stringReplaced;
      return stringReplaced;
    }
    case 'OneYear': {
      let stringReplaced = myAutoesclusionPage.labelAutoesclusioneConclusa.replace(
        '${date}',
        format(
          add(new Date(), {
            years: 1,
          }),
          'dd/MM/yyyy'
        )
      );
      stringReplaced = amount ? stringReplaced.replace('${amount}', amount) : stringReplaced;
      return stringReplaced;
    }
    case 'Permanent':
      return myAutoesclusionPage.labelAutoesclusioneConclusaPermanente;
    default:
      return undefined;
  }
};

export const int2roman = (original: number): string => {
  if (original === 0) {
    return 'I';
  }
  if (original < 0 || original > 3999) {
    throw new Error('Error: Input integer limited to 1 through 3,999');
  }

  const numerals = [
    ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'], // 1-9
    ['X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC'], // 10-90
    ['C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM'], // 100-900
    ['M', 'MM', 'MMM'], // 1000-3000
  ];

  // TODO: Could expand to support fractions, simply rounding for now
  const digits = Math.round(original).toString().split('');
  let position = digits.length - 1;

  return digits.reduce((roman, digit) => {
    if (digit !== '0') {
      roman += numerals[position][parseInt(digit) - 1];
    }

    position -= 1;

    return roman;
  }, '');
};

export const populateGames = (
  giochi: {
    codice: string | null;
    produttore: string | null;
    descrizione: string | null;
    verticale: string | null;
  }[]
) => {
  return giochi.map((game) => {
    if (game.produttore && game.descrizione) {
      return {
        key: game.codice,
        name: `${game.produttore}, ${game.descrizione}`,
      };
    } else if (game.produttore) {
      return {
        key: game.codice,
        name: game.produttore,
      };
    } else {
      return {
        key: game.codice,
        name: game.descrizione,
      };
    }
  });
};

export const getVerticalString = (vertical: string) => {
  if (vertical === 'Generico') {
    return '';
  }
  return vertical;
};

export const populateDetailedSystemTableRow = (
  item: SystemProps,
  labelSystem: {
    labelSistema: string | undefined;
    labelMultiple: string | undefined;
    labelPuntata: string | undefined;
  }
) => {
  let data: TableFieldProps[] = [
    {
      component: TableField,
      props: {
        label: labelSystem.labelSistema,
        value: item.tipologia,
      },
    },
    {
      component: TableField,
      props: {
        label: labelSystem.labelMultiple,
        value: item.numColonne,
      },
    },
    {
      component: TableField,
      props: {
        label: labelSystem.labelPuntata,
        value: formatter.format(getAmount(item.importo)),
      },
    },
  ];
  return data;
};

export const checkSession = async (router: NextRouter) => {
  if (isSeamlessUrl(router.asPath) || isCORS()) return;
  const isLogged = isTruthy(cookie.getItem(IS_LOGGED));
  const isFromRegistration = isTruthy(sessionStorage.getItem(FROM_REGISTRATION));
  const username = router.query['snai-carta'];
  const password = router.query['snai-token'];
  const clientType = router.query['client-type'];
  if (!isLogged && !isFromRegistration && (!username || !password || !clientType)) {
    router.push('/sport');
  }
};

export const getTypologyByString = (
  typology: string | null | undefined,
  labels: {
    labelSingola?: string;
    labelSistema?: string;
    labelMultipla?: string;
    labelImportoGiocato?: string;
    labelQuotaTotale?: string;
    labelPossibileVincita?: string;
    labelEventi?: string;
    labelEsiti?: string;
    labelVerificaCashout?: string;
    labelStato?: string;
    labelVincita?: string;
    labelEmesso?: string;
  }
) => {
  switch (typology?.toLowerCase()) {
    case 'sistema':
      return labels.labelSistema;
    case 'multipla':
      return labels.labelMultipla;
    case 'singola':
      return labels.labelSingola;
    default:
      return typology;
  }
};

export const trasformStateHappybet = (state?: string | null) => {
  if (isHappybetSite) {
    switch (state) {
      case 'Rückkauf':
      case 'Payback':
      case 'Cashout':
        return 'Chiusa con cash out';
      case 'Bezahlt':
      case 'Paid':
      case 'Ücretli':
        return 'Vinta';
      default:
        return state;
    }
  } else return state;
};

export const trasformStateinHappybetLang = (labelMyBetOpen: string, state?: string | null) => {
  if (isHappybetSite) {
    switch (state) {
      case 'Aperta':
        return labelMyBetOpen;
      default:
        return state;
    }
  } else return state;
};

export const isPositive = (num?: number | string | null) => {
  const n = Number(num);
  if (isNaN(n)) return false;
  return n > 0;
};

function vediMovimento(cod_transazione, conto, token, pvd, cod_applicazione) {
  console.log('vediMovimento', cod_transazione, conto, token, pvd, cod_applicazione);
  switch (parseInt(cod_applicazione)) {
    case 1:
      //@ts-ignore
      var nve = new GiochiNumerici.Superenalotto(GiochiNumerici.Constants.POPUP_VIEW_MODE, null);
      nve.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 2:
      //@ts-ignore
      var wfl = new GiochiNumerici.WflClassic(GiochiNumerici.Constants.POPUP_VIEW_MODE, '');
      wfl.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 5:
      //@ts-ignore
      var wfl = new GiochiNumerici.WflGra(GiochiNumerici.Constants.POPUP_VIEW_MODE, '');
      wfl.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 6:
      //@ts-ignore
      var wfl = new GiochiNumerici.WflCas(GiochiNumerici.Constants.POPUP_VIEW_MODE, '');
      wfl.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 7:
      //@ts-ignore
      var svt = new GiochiNumerici.SiVinceTutto(GiochiNumerici.Constants.POPUP_VIEW_MODE, null);
      svt.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 8:
      //@ts-ignore
      var ej = new GiochiNumerici.Eurojackpot(GiochiNumerici.Constants.POPUP_VIEW_MODE, 'id');
      ej.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 9: //VinciCasa
      //@ts-ignore
      var vic = new GiochiNumerici.VinciCasa(GiochiNumerici.Constants.POPUP_VIEW_MODE, '');
      vic.getDettaglio(cod_transazione, conto, token, pvd);
      break;
    case 10: //Playsix
      // tyche.showTicket(pvd, conto, token, cod_transazione, "win");
      break;
    case 27: //PlayYourDate
      var pydObj = {
        cod_transazione: cod_transazione,
        conto: conto,
        token: token,
        sisalID: pvd,
        cod_applicazione: cod_applicazione,
      };
      var hash = btoa(JSON.stringify(pydObj));
      var width = 770;
      var height = 655;
      var leftPosition = window.screen.width / 2 - width / 2;
      var topPosition = window.screen.height / 2 - height / 2;
      var features =
        'fullscreen=0,titlebar=0,toolbar=0,location=0,status=0,menubar=0,scrollbars=0,resizable=0,width=' +
        width +
        ',height=' +
        height;
      features +=
        'left=' + leftPosition + ',top=' + topPosition + ',screenX=' + leftPosition + ',screenY=' + topPosition;
      var url = '/api/get-sisal-detail/' + hash;
      window.open(url, 'popup', features);
      break;
  }
}

export const openLotteryPopup = async (
  ticket: {
    idTransazione: string;
    codTipoGioco: number;
    tipoConcorso: string | null;
    descriptionGame: string;
    codApplicazione?: string;
    idConcorso?: string;
  },
  session: DeepPartial<Session>
) => {
  // const userData = {
  //   card: session.user.cardNumber,
  //   token: session.user.token,
  //   username: session.user.name,
  //   cod_tipo_gioco: ticket.codTipoGioco,
  //   tipo_concorso: ticket.tipoConcorso,
  // };
  const conto = session?.user?.contractCode;
  let dataSent;

  const data = {
    sisalID: process.env.NEXT_PUBLIC_SISAL_ID_RIVENDITORE_SISAL,
    gevID: process.env.NEXT_PUBLIC_SISAL_ID_RIVENDITORE_LOTTOMATICA,
    ldtID: process.env.NEXT_PUBLIC_SISAL_ID_RIVENDITORE_LOTTOMATICA,
    lottoUri: `https://vetrina.giocodellotto.it/${
      VETRINA_LOTTERY[`TYPE_${ticket.descriptionGame}`]
    }/dettaglio/dettaglioGiocata.do?skin=black&idRivenditore=${
      process.env.NEXT_PUBLIC_SISAL_ID_RIVENDITORE_LOTTOMATICA
    }&dominio=classic.snai.it`,
  };

  if (ticket.codTipoGioco >= 90 && ticket.codTipoGioco <= 93) {
    const externalToken = await getTokenBySupplierType(SUPPLIER_SISAL_MODEL_API_KEY);
    //solo sisal
    if (!isTruthy(externalToken) && !isTruthy(data.sisalID)) {
      // const dataToSend = {
      //   cod_transazione: ticket.idTransazione,
      //   conto: conto,
      //   token: ticket.externalToken,
      //   sisalID: ticket.sisalID,
      //   cod_applicazione: ticket.codApplicazione,
      // };
      vediMovimento(ticket.idTransazione, conto, externalToken, data.sisalID, ticket.codApplicazione);
      dataSent = true;
    }
  }
  if (!dataSent) {
    switch (ticket.codTipoGioco) {
      case 90: //sisal superenalotto
      case 91: //sisal win for life
      case 92: //sisal playsix / play your date
      case 93: {
        //sisal vinci casa
        const externalToken = await getTokenBySupplierType(SUPPLIER_SISAL_MODEL_API_KEY);
        vediMovimento(ticket.idTransazione, conto, externalToken, data.sisalID, ticket.codApplicazione);
        break;
      }

      case 94: {
        //lottomatica
        const externalToken = await getTokenBySupplierType(SUPPLIER_LOTTOMATICA_MODEL_API_KEY);
        var width = 770;
        var height = 655;
        var leftPosition = window.screen.width / 2 - width / 2;
        var topPosition = window.screen.height / 2 - height / 2;
        var features =
          'fullscreen=0,titlebar=0,toolbar=0,location=0,status=0,menubar=0,scrollbars=0,resizable=0,width=' +
          width +
          ',height=' +
          height;
        features +=
          'left=' + leftPosition + ',top=' + topPosition + ',screenX=' + leftPosition + ',screenY=' + topPosition;
        var url = data.lottoUri;
        url += '&idContoGioco=' + conto;
        url += '&idTransazione=' + ticket.idTransazione;
        url += '&token=' + externalToken;
        // if (!!windowOpenInstance) {
        //   windowOpenInstance.location = url;
        // } else {
        window.open(url, 'popup', features);
        // }
        break;
      }

      case 180: {
        //gratta e vinci
        //@ts-ignore
        if (typeof replayLauncher === 'function') {
          //console.log('GEV', typeof(replayLauncher), ticket.idConcorso, conto, data.token, ticket.id_tran, data.gevID);
          const externalToken = await getTokenBySupplierType(SUPPLIER_GRATTA_E_VINCI_MODEL_API_KEY);

          var width = 400;
          var height = 400;
          var leftPosition = window.screen.width / 2 - width / 2;
          var topPosition = window.screen.height / 2 - height / 2;
          var features =
            'fullscreen=0,titlebar=0,toolbar=0,location=0,status=0,menubar=0,scrollbars=0,resizable=0,width=' +
            width +
            ',height=' +
            height;
          features +=
            'left=' + leftPosition + ',top=' + topPosition + ',screenX=' + leftPosition + ',screenY=' + topPosition;
          // impostazione dell'URL
          var url = 'https://lit.grattaevinci.com' + '/gioco-igp/template/ricevuta.jsp';
          url += '?IDConcorso=' + ticket.idConcorso;
          url += '&IDContoGioco=' + conto;
          url += '&Token=' + externalToken;
          url += '&IDTransazione=' + ticket.idTransazione;
          url += '&IDRivenditore=' + data.gevID;
          // if (!!windowOpenInstance) {
          //   windowOpenInstance.location = url;
          // } else {
          //@ts-ignore
          // replayLauncher(ticket.idConcorso, conto, ticket.externalToken, ticket.idTransazione, data.gevID);
          window.open(url, 'popup', features);
          break;
          // }
        } else return alert('Librerie Gratta e Vinci non caricate');
      }

      case 181: {
        //lotteria italia
        openLotteriaItalia(ticket.idConcorso, ticket.idTransazione, data.ldtID, conto);
        break;
      }
    }
  }
};

const openLotteriaItalia = async (idConcorso?: string, idTransazione?: string, ldtID?: string, accountId?: string) => {
  const token = await getTokenBySupplierType(SUPPLIER_LOTTERIA_ITALIA_MODEL_API_KEY);

  const onMessage = (event: any) => {
    const data = Reflect.get(event ?? {}, 'data') ?? {};
    const { action, channelMessage } = data ?? {};

    if (action === 'ASK-CREDENTIAL' && channelMessage === 'VETRINALOTITA-DETAIL') {
      window.removeEventListener('message', onMessage);

      window.postMessage(
        { action: 'SEND-CREDENTIAL', channelMessage: 'VETRINALOTITA-DETAIL', data: { token, accountId } },
        '*'
      );
    }
  };

  window.addEventListener('message', onMessage);

  //@ts-ignore
  window.receiptDetails(idConcorso, idTransazione, ldtID);
};
